import { Select, Tag, Tooltip } from "antd"
import React from "react"

// Close to the server's EmailField: a dotted domain ending in a label of two
// or more letters, so the browser does not pass what the server then refuses.
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[A-Za-z]{2,}$/

const isEmailAddress = (address: string) => EMAIL.test(address)

/** Each address once, case-insensitively, leaving out any in `besides`. */
const uniqueAddresses = (addresses: string[], besides: string[] = []) => {
  const seen = new Set(besides.map((address) => address.toLowerCase()))
  const kept: string[] = []
  for (const raw of addresses) {
    const address = raw.trim()
    if (address && !seen.has(address.toLowerCase())) {
      seen.add(address.toLowerCase())
      kept.push(address)
    }
  }
  return kept
}

interface EmailRecipientsInputProps {
  id: string
  ariaLabel: string
  /** An address always on the line and not removable (the lead's customer). */
  fixed?: string
  fixedHint?: string
  /** The addresses added besides `fixed`. */
  value: string[]
  onChange: (addresses: string[]) => void
  placeholder?: string
  /** Addresses to leave out when added here, because another line has them. */
  besides?: string[]
}

/**
 * A line of email addresses, typed and committed by Enter, a comma, a
 * semicolon or a space. An address that isn't one is kept and shown in red,
 * so the person sees what to fix rather than losing what they typed.
 */
function EmailRecipientsInput({
  id,
  ariaLabel,
  fixed,
  fixedHint,
  value,
  onChange,
  placeholder,
  besides = []
}: EmailRecipientsInputProps) {
  const shown = fixed ? [fixed, ...value] : value
  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      mode={"tags"}
      className={"w-full"}
      value={shown}
      // There is nothing to choose from, so the list would only echo the
      // typing back — and pressing that echo removes the address again.
      open={false}
      suffixIcon={null}
      tokenSeparators={[",", ";", " "]}
      placeholder={placeholder}
      onChange={(next: string[]) =>
        onChange(
          uniqueAddresses(
            next.filter((address) => address !== fixed),
            [...(fixed ? [fixed] : []), ...besides]
          )
        )
      }
      tagRender={({ value: address, closable, onClose }) => {
        const text = String(address)
        const isFixed = !!fixed && text === fixed
        const tag = (
          <Tag
            className={"!my-0.5 !me-1"}
            color={isEmailAddress(text) ? undefined : "error"}
            closable={closable && !isFixed}
            onClose={onClose}
            onMouseDown={(event) => event.preventDefault()}
          >
            {text}
          </Tag>
        )
        return isFixed && fixedHint ? (
          <Tooltip title={fixedHint}>{tag}</Tooltip>
        ) : (
          tag
        )
      }}
    />
  )
}

export { EmailRecipientsInput, isEmailAddress }
